import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CreateProcessInput, ProcessAgentOption, ProcessSkillOption } from '@/api/processes-api';
import { ProcessFormValues, ProcessFormTriggerType, ProcessMemberOption, Work4MeProcessForm } from './Work4MeProcessForm';
import { useMemo, useState } from 'react';

interface Work4MeProcessCreateProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: CreateProcessInput) => Promise<void>;
  isSubmitting?: boolean;
  agentOptions: ProcessAgentOption[];
  skillOptions: ProcessSkillOption[];
  memberOptions: ProcessMemberOption[];
  xeroConnectionActive: boolean;
}

const TRIGGER_TYPE_LABELS: Record<ProcessFormTriggerType, string> = {
  TAG_APPLIED: 'Tag applied',
  MESSAGE_IMPORTED: 'Message imported',
  EMAIL_RECEIVED: 'Email received',
  SCHEDULE: 'Schedule',
  XERO_EVENT: 'Xero event',
};

export function Work4MeProcessCreate({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting = false,
  agentOptions,
  skillOptions,
  memberOptions,
  xeroConnectionActive,
}: Work4MeProcessCreateProps) {
  const [triggerType, setTriggerType] = useState<ProcessFormTriggerType>('TAG_APPLIED');

  const initialValues: ProcessFormValues = useMemo(() => ({
    name: '',
    description: '',
    triggerType,
    tagId: 0,
    directions: [],
    matchCriteria: '',
    cron: '',
    timezone: '',
    runAsMemberId: null,
    xeroEventCategory: 'INVOICE',
    xeroEventTypes: [],
    xeroInvoiceTypes: [],
    xeroInvoiceStatuses: [],
    agentId: agentOptions[0]?.id ?? 0,
    skillIds: [],
    inputTemplate: '',
    enabled: true,
  }), [agentOptions, triggerType]);

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
  };

  const buildCreateInput = (values: ProcessFormValues): CreateProcessInput => {
    if (values.agentId === null) {
      throw new Error('Process create requires a member agent');
    }
    const base = {
      name: values.name,
      description: values.description,
      agentId: values.agentId,
      ...(values.skillIds?.length ? { skillIds: values.skillIds } : {}),
      inputTemplate: values.inputTemplate,
      enabled: values.enabled,
    };
    if (values.triggerType === 'MESSAGE_IMPORTED') {
      return {
        ...base,
        trigger: {
          type: 'MESSAGE_IMPORTED',
          config: values.directions.length > 0 ? { directions: values.directions } : {},
        },
      };
    }
    if (values.triggerType === 'EMAIL_RECEIVED') {
      return {
        ...base,
        trigger: { type: 'EMAIL_RECEIVED', config: { matchCriteria: values.matchCriteria } },
      };
    }
    if (values.triggerType === 'SCHEDULE') {
      return {
        ...base,
        trigger: {
          type: 'SCHEDULE',
          config: {
            cron: values.cron,
            timezone: values.timezone,
            ...(values.runAsMemberId !== null ? { runAsMemberId: values.runAsMemberId } : {}),
          },
        },
      };
    }
    if (values.triggerType === 'XERO_EVENT') {
      const invoiceScoped = values.xeroEventCategory === 'INVOICE';
      return {
        ...base,
        trigger: {
          type: 'XERO_EVENT',
          config: {
            eventCategory: values.xeroEventCategory,
            eventTypes: values.xeroEventTypes,
            ...(invoiceScoped && values.xeroInvoiceTypes.length > 0 ? { invoiceTypes: values.xeroInvoiceTypes } : {}),
            ...(invoiceScoped && values.xeroInvoiceStatuses.length > 0
              ? { invoiceStatuses: values.xeroInvoiceStatuses }
              : {}),
            ...(values.runAsMemberId !== null ? { runAsMemberId: values.runAsMemberId } : {}),
          },
        },
      };
    }
    return {
      ...base,
      trigger: { type: 'TAG_APPLIED', config: { tagId: values.tagId } },
    };
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className="w-screen sm:w-[560px] flex flex-col px-0">
        <SheetHeader className="px-4">
          <SheetTitle>Create Process</SheetTitle>
          <SheetDescription>
            Configure a background process and the trigger that runs it.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          <div>
            <Label htmlFor="process-trigger-type">Trigger</Label>
            <Select value={triggerType} onValueChange={(value) => setTriggerType(value as ProcessFormTriggerType)}>
              <SelectTrigger id="process-trigger-type" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(TRIGGER_TYPE_LABELS) as ProcessFormTriggerType[])
                  .filter((type) => type !== 'XERO_EVENT' || xeroConnectionActive)
                  .map((type) => (
                  <SelectItem key={type} value={type}>
                    {TRIGGER_TYPE_LABELS[type]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Work4MeProcessForm
            initialValues={initialValues}
            isSubmitting={isSubmitting}
            submitLabel="Create Process"
            submittingLabel="Creating..."
            agentOptions={agentOptions}
            skillOptions={skillOptions}
            memberOptions={memberOptions}
            onSubmit={async (values) => {
              await onSubmit(buildCreateInput(values));
              onOpenChange(false);
            }}
          />
        </div>

        <SheetFooter className="px-4">
          <div className="w-full">
            <Separator className="mb-4" />
            <p className="text-xs text-muted-foreground">Fill all required fields to create a process.</p>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
